<script>
  import axios from 'axios';
  import { afterUpdate, createEventDispatcher, onMount } from 'svelte';
  import { clickToCopy as c2c } from '@util/click-to-copy';
  import Animate from '@ui/pico/animate.svelte';
  import Icon from '@ui/pico/icon.svelte';
  import Heading from '@ui/pico/heading.svelte';
  import Pico from '@ui/pico/pico.svelte';

  import Snippet from './Snippet.svelte';
  import { components } from './lib/store';
  import { manifestUrl } from './lib/config';
  import { defaults } from '../../../apps/webforma/src/config';

  let manifest;
  let snippetContent;
  let latest = '';
  let versions = '';
  let selectedAttrIdx = null;
  let selectedInputIdx = null;

  export let forms = [];
  export let fid;

  $: formCode = forms?.find((f) => f._id === fid)?.formLinkCode || '';
  $: preview = 'code';
  $: currentInputIdx = 0;

  $: scriptSnippet = `<script 
  src="${defaults.cdnUrl}/webforma.${selectedVersion}.${selectedFileType}" 
  integrity="${sha}" 
  crossorigin="anonymous" defer="defer">
<\/script>`;
  $: formOpenTag = `\n<!-- Copy the code below to the <body> of your site. --> \n \n<wf-form code="${formCode}">`;
  $: formCloseTag = ` \n   <input type="submit" />\n</wf-form> \n \n <!-- Copy the code below to the <head> of your site. --> \n \n ${scriptSnippet} \n `;
  $: formInputs = ``;
  $: formSnippet = formOpenTag + formInputs + formCloseTag;
  $: formSnippetContent = formOpenTag + buildFormInputs(false) + formCloseTag;
  $: selectedVersion = '';
  $: selectedFileType = 'js';
  $: sha =
    manifest?.packages[`webforma.${selectedVersion}.${selectedFileType}`]
      ?.sha256;

  onMount(async () => {
    manifest = (await getManifest()) || {};
    selectedVersion = 'v' + manifest.latest.version;
    latest = manifest.latest.version;
    versions = manifest.versions.slice(1);
    sha =
      manifest.packages[`webforma.${selectedVersion}.${selectedFileType}`]
        .sha256;
    formSnippet = `${formOpenTag}${formInputs}${formCloseTag}`;
  });

  afterUpdate(() => {
    if (preview !== 'form') {
      buildFormInputs(true);
    }
  });

  const dispatch = createEventDispatcher();

  function buildFormInputs(showLineNumbers) {
    formInputs = [...$components.formBuilder.inputs].reduce(
      (acc, input, idx) => {
        let lineNumber = showLineNumbers ? idx + 1 : '';

        let attrs = input.attrs.reduce((attrString, attr) => {
          if (!attr.name) {
            return `${attrString}`;
          }
          return `${attrString} ${attr.name}="${attr.value}"`;
        }, '');

        return (
          acc +
          `\n${lineNumber}  <wf-input type="${input.type}" name="${
            input.name
          }" placeholder="${input.placeholder || ''}"${attrs || ''}></wf-input>`
        );
      },
      ''
    );

    return formInputs;
  }

  let inputTypes = [
    'number',
    'date',
    'email',
    '----------------',
    'button',
    'checkbox',
    'color',
    'date',
    'datetime-local',
    'email',
    'file',
    'hidden',
    'image',
    'month',
    'number',
    'password',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
    'tel',
    'text',
    'time',
    'url',
    'week',
  ];

  async function getManifest() {
    const { data } = await axios.get(manifestUrl);
    return data;
  }
  function addInput(idx) {
    const inputName = $components.formBuilder.inputs[idx].name;
    const inputType = $components.formBuilder.inputs[idx].type;
    $components.formBuilder.inputs = [
      ...$components.formBuilder.inputs,
      {
        name: '',
        type: 'text',
        placeholder: '',
        userInput: false,
        attrs: [{ name: '', value: '' }],
      },
    ];

    currentInputIdx = $components.formBuilder.inputs.length - 1;
    document.querySelector('.name-input').focus();
  }
  function removeInput(idx) {
    const inputs = $components?.formBuilder?.inputs;
    if (currentInputIdx > 0) {
      currentInputIdx--;
    }
    if ($components?.formBuilder?.inputs.length === 1) {
      $components.formBuilder.inputs[idx] = {
        name: '',
        type: 'text',
        placeholder: '',
        userInput: false,
        attrs: [
          {
            name: '',
            value: '',
          },
        ],
      };
      idx = null;
    }

    $components.formBuilder.inputs = [
      ...inputs.filter((item, i) => {
        return i !== idx;
      }),
    ];
  }
  function addAttr(idx) {
    $components.formBuilder.inputs[idx].attrs = [
      ...$components.formBuilder.inputs[idx].attrs,
      {
        name: '',
        value: '',
      },
    ];
  }
  function removeAttr(idx, aidx) {
    const attrs = $components.formBuilder.inputs[idx].attrs;

    if (attrs.length === 1) {
      attrs[aidx] = {
        name: '',
        value: '',
      };
      aidx = null;
    }

    $components.formBuilder.inputs[idx].attrs = [
      ...attrs.filter((item, i) => {
        return i !== aidx;
      }),
    ];
  }
  function attributeFocus(el, idx) {
    return {
      update(newIdx) {
        if (Number(el.getAttribute('data-idx')) === newIdx) {
          el.toggleAttribute('open', true);
          el.querySelector('input').focus();
          el.querySelector('input').onblur = () => {
            selectedAttrIdx = null;
          };
        }
      },
    };
  }
  function inputFocus(el, idx) {
    return {
      update(newIdx) {
        if (Number(el.getAttribute('data-idx')) === newIdx) {
          el.querySelector('details').toggleAttribute('open');
          el.querySelector('input').focus();
          el.querySelector('input').onblur = () => {
            selectedInputIdx = null;
          };
        }
      },
    };
  }
  function resetSnippet() {
    if (confirm('Are you sure you want to reset your snippet?')) {
      formCode = '';

      $components.formBuilder.inputs = [
        {
          name: '',
          type: 'text',
          placeholder: '',
          userInput: false,
          attrs: [
            {
              name: '',
              value: '',
            },
          ],
        },
      ];

      formSnippet = `${formOpenTag}${formInputs}${formCloseTag}`;
      currentInputIdx = 0;
    }
  }
  function handleInputKeyUp(ev, idx) {
    if (ev.key === 'Enter' && ev.shiftKey) {
      selectedAttrIdx = idx;
      return;
    }
    if (ev.key === 'Enter') {
      addInput(idx);
    }
    if (
      (ev.key === 'Backspace' && ev.shiftKey) ||
      (ev.key === 'Delete' && ev.shiftKey)
    ) {
      removeInput(idx);
    }
  }
  function handleAttrKeyUp(ev, idx, aidx) {
    if (ev.key === 'Enter' && ev.shiftKey) {
      selectedInputIdx = idx;
      return;
    }
    if (ev.key === 'Enter') {
      addAttr(idx);
    }
    if (ev.key === 'Backspace' && ev.target.value === '') {
      removeAttr(idx, aidx);
    }
  }
  function handlePlaceholder(ev, idx) {
    if ($components.formBuilder.inputs[currentInputIdx].userInput) return;

    $components.formBuilder.inputs[currentInputIdx].placeholder =
      ev.target.value;
  }
  function handleBodyKeyDown(ev) {
    if ((ev.which || ev.code) === 67 && ev.altKey) {
      preview = 'code';
      return;
    }
    if ((ev.which || ev.code) === 70 && ev.altKey) {
      preview = 'form';
      buildFormInputs(false);
      return;
    }
  }
  function handlePreview(view, showInputs) {
    return () => {
      preview = view;
      buildFormInputs(showInputs);
    };
  }
  function formUpdated(ev) {
    const el = ev.target.querySelector('option:checked');
    const fid = el.getAttribute('data-fid');
    const sid = el.getAttribute('data-sid');
    const formLinkCode = el.getAttribute('value');

    dispatch('formUpdated', { formLinkCode, fid, sid });
  }
</script>

<Pico />
<svelte:body on:keydown={(ev) => handleBodyKeyDown(ev)} />

<!-- <Page> -->
<article class="widget-container">
  <div class="component-builder grid">
    <Heading title="Step 1: Select Form and Webforma Version" smaller />
    <div class="step-one form-snip pet-container">
      <div class="selection-bar">
        <select value={formCode} on:change={formUpdated}>
          <option value="" disabled selected>
            <span class="greyed">Choose Form</span>
          </option>
          {#each forms.sort( (a, b) => (a?.site?.domain < b?.site?.domain ? -1 : 1) ) as form}
            <option
              value={form.formLinkCode}
              data-sid={form.sid}
              data-fid={form._id}
            >
              {form?.site?.domain} - {form.name}
            </option>
          {/each}
        </select>
        <div
          class="preview-selector"
          on:keydown={null}
          on:click={handlePreview('code', true)}
        >
          <Icon size="30" name="code" />
        </div>
        <div
          class="preview-selector"
          on:keydown={null}
          on:click={handlePreview('form', false)}
        >
          <Icon size="30" name="eye" />
        </div>
      </div>
      <div class="version-bar">
        <select class="version" bind:value={selectedVersion}>
          <option value="v{latest}">v{latest} - latest</option>
          {#each versions as version}
            <option value="v{version}">v{version}</option>
          {/each}
        </select>

        <select class="file-type" bind:value={selectedFileType}>
          <option value="js">.js</option>
          <option value="mjs">.mjs</option>
          <option value="cjs">.cjs</option>
        </select>
      </div>
    </div>

    <div class="step-two">
      <Heading title="Step 2: Build Form Inputs" smaller />
      {#if preview !== 'form'}
        <table>
          <tr>
            <th class="input-number-th">#</th>
            <th>Input Type</th>
            <th>Input Name</th>
            <th>Placeholder</th>
            <th>Add/Remove</th>
          </tr>
          <tr
            class="inputs-container"
            use:inputFocus={selectedInputIdx}
            data-idx={currentInputIdx}
          /><tr class="inputs-node">
            <td class="input-number-td">
              <span>
                {currentInputIdx + 1}
              </span>
            </td>
            <td>
              {#if $components.formBuilder.inputs[currentInputIdx]}
                <select
                  required
                  bind:value={$components.formBuilder.inputs[currentInputIdx]
                    .type}
                >
                  {#each inputTypes as itype}
                    <option value={itype}>
                      {itype}
                    </option>
                  {/each}
                </select>
              {/if}
            </td>
            <td>
              {#if $components.formBuilder.inputs[currentInputIdx]}
                <input
                  class="name-input"
                  type="text"
                  placeholder="Enter Input Name"
                  required
                  on:keyup={(ev) => {
                    handleInputKeyUp(ev, currentInputIdx);
                  }}
                  bind:value={$components.formBuilder.inputs[currentInputIdx]
                    .name}
                  on:input={(ev) => {
                    handlePlaceholder(ev, currentInputIdx);
                  }}
                />
              {/if}
            </td>
            <td>
              {#if $components.formBuilder.inputs[currentInputIdx]}
                <input
                  type="text"
                  placeholder="Enter Placeholder"
                  required
                  on:keyup={(ev) => {
                    handleInputKeyUp(ev, currentInputIdx);
                  }}
                  bind:value={$components.formBuilder.inputs[currentInputIdx]
                    .placeholder}
                  on:keydown={() =>
                    ($components.formBuilder.inputs[
                      currentInputIdx
                    ].userInput = true)}
                />
              {/if}
            </td>
            <td>
              <div class="input-controls">
                <div class="preview-selector">
                  <Icon
                    size="30"
                    name="plus"
                    on:click={() => addInput(currentInputIdx)}
                  />
                </div>

                <div class="preview-selector">
                  <Icon
                    size="30"
                    name="minus"
                    on:click={() => removeInput(currentInputIdx)}
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <!-- Begin Attributes Container -->
            <td colspan="4">
              <details
                use:attributeFocus={selectedAttrIdx}
                data-idx={currentInputIdx}
              >
                <summary>Add Attributes</summary>

                {#if $components.formBuilder.inputs[currentInputIdx]}
                  <table class="attributes-cell">
                    <tr>
                      <th>Attribute Name</th>
                      <th>Attribute Value</th>
                      <th>Add / Remove</th>
                    </tr>

                    {#each $components?.formBuilder?.inputs[currentInputIdx].attrs as attr, aidx}
                      <tr>
                        <td>
                          <input
                            type="text"
                            placeholder="Name"
                            required
                            name="name"
                            on:keyup={(ev) => {
                              handleAttrKeyUp(ev, currentInputIdx, aidx);
                            }}
                            bind:value={$components.formBuilder.inputs[
                              currentInputIdx
                            ].attrs[aidx].name}
                          />
                        </td>
                        <td>
                          <input
                            name="value"
                            type="text"
                            placeholder="Value"
                            required
                            bind:value={$components.formBuilder.inputs[
                              currentInputIdx
                            ].attrs[aidx].value}
                          />
                        </td>
                        <td class="attribute-controls">
                          <div
                            class="preview-selector"
                            type="button"
                            alt="Add Attributes"
                            on:keydown={null}
                            on:click={() => {
                              addAttr(currentInputIdx);
                            }}
                          >
                            <Icon size="30" name="plus" />
                          </div>
                          <div
                            class="preview-selector"
                            type="button"
                            on:keydown={null}
                            on:click={() => {
                              removeAttr(currentInputIdx, aidx);
                            }}
                          >
                            <Icon size="30" name="minus" />
                          </div>
                        </td>
                      </tr>
                    {/each}
                    <!-- End Attributes Container -->
                  </table>
                {/if}
              </details>
            </td>
          </tr>
        </table>
      {/if}
      {#if preview === 'code'}
        <Animate name="fadeIn" startVisible>
          <div class="snippet-container">
            <div class="input-selector-container">
              <select placeholder="select" bind:value={currentInputIdx}>
                <option value={currentInputIdx} selected
                  >Select an Input to Edit</option
                >
                {#each $components?.formBuilder?.inputs as selector, idx}
                  {#if selector.name}
                    <option value={idx}>
                      {idx + 1}.
                      {selector?.name}
                    </option>
                  {:else}
                    <option value={idx}>
                      {idx + 1}. (No Name Entered)
                    </option>
                  {/if}
                {/each}
              </select>
            </div>
            <Snippet
              language="html"
              codeSnippet={formSnippet}
              padding="1em 0 0 0"
            />

            <span bind:this={formSnippet} style="display:none;">
              {formSnippet}
            </span>

            <div class="snippet-controls">
              <Icon name="refresh-ccw" size="25" on:click={resetSnippet} />
              <div use:c2c={formSnippetContent}>
                <Icon name="copy" size="25" />
              </div>
            </div>
          </div>
        </Animate>
      {:else}
        <h2>Form Preview</h2>
        <div class="form-preview-container">
          <Animate name="fadeIn" startVisible>
            <article class="snippet-container">
              {@html formSnippet}
            </article>
          </Animate>
        </div>
      {/if}
    </div>
  </div>
</article>

<!-- </Page> -->
<style>
  :root {
    --gap: 0.2rem;
  }
  .input-number-td,
  .input-number-th {
    width: 60px;
    text-align: center;
  }
  .input-number-th {
    text-align: center;
    padding: 0;
  }
  .input-number-td {
    height: 61px;
    font-weight: 700;
    padding: 0;
  }
  th,
  tr,
  td {
    padding: 0 0 0 0;
    border-bottom: none;
  }
  .input-controls {
    display: flex;
    align-content: center;
    gap: var(--gap);
  }

  td:last-child {
    padding-right: 0;
  }

  td {
    padding-right: 1em;
  }

  th {
    z-index: 1;
    font-size: 0.75rem;
    border-bottom: 1px solid #cccccc;
    padding: 0.5em;
    padding-left: 0;
  }
  th:last-child {
    text-align: center;
  }

  select {
    padding: 0 1em;
  }
  select,
  input {
    height: 40px;
    margin-bottom: 0;
  }
  tr {
    padding-bottom: 0;
    margin-bottom: 0;
  }
  table {
    margin: 0;
    border-spacing: 1em !important;
  }
  table:nth-child(1) {
    border: none;
    top: 0;
    z-index: 3;
    border: 1px solid var(--contrast);
    box-shadow: 0 8px 12px -12px black;
  }

  table:nth-child(1) td {
    border: none;
  }

  table:nth-child(1) tr td input:first {
    padding-left: 2em !important;
  }
  details,
  table {
    margin: 0;
  }
  summary {
    margin: 0.25em 0 1em 0;
  }
  details {
    padding-bottom: 1em;
    margin-left: 57px;
    width: 85%;
  }
  td {
    margin: 0;
  }
  details > summary:first-of-type {
    display: list-item;
    counter-increment: list-item 0;
    list-style: none !important;
  }
  details summary::-webkit-details-marker {
    display: none !important;
  }
  details summary {
    list-style-type: none !important;
  }
  details summary::after {
    position: relative;
    left: -10px;
    float: left;
  }
  details,
  summary {
    border: none;
  }

  button {
    /* border: none; */

    /* background: none; */
    /* width: fit-content; */
    padding: 0;
    /* line-height: 0; */
    /* height: 50px; */
  }
  :global(.input-controls svg) {
    /* stroke-width: 0.5px !important;
    padding: 0;
    border: 1px solid #999999;
    stroke: #999999;
    border-radius: var(--border-radius);
    cursor: pointer; */
  }
  .inputs-container {
    margin-top: 1em;
  }

  .input-selector-container {
    position: absolute;
    right: 0;
    margin: 1em;
    width: 300px;
    z-index: 3;
    color: #ffffff;
  }

  .input-selector-container select {
    color: #ffffff;
    font-size: 14px;
  }

  .attribute-controls {
    display: flex;
    justify-content: center;
    gap: var(--gap);
  }
  .component-builder {
    padding-top: 0px;
  }
  .snippet-container {
    margin-top: 1em;
    position: relative;
  }
  .snippet-controls {
    display: flex;
    justify-content: space-around;
    width: 100px;
    position: relative;
    right: 0;
    float: right;
    top: -55px;
    right: 7px;
  }
  :global(.snippet-controls svg) {
    stroke: #ffffff;
  }
  :global(.snippet-controls span) {
    cursor: pointer;
  }
  .greyed {
    color: red;
  }
  .version {
    width: 100%;
    margin-bottom: 1em;
  }
  .file-type {
    width: fit-content;
  }
  .widget-container {
    width: 100%;
    margin: 0;
    padding: 2em;
  }

  .component-builder {
    grid-template-columns: 1fr;
  }

  .step-one {
    display: flex;
    flex-direction: column;
    gap: calc(var(--gap) * 3);
  }
  .step-two {
    padding-top: 2rem;
  }
  .selection-bar {
    display: flex;
    flex-direction: row;
    gap: var(--gap);
  }
  .version-bar {
    display: flex;
    flex-direction: row;
    gap: var(--gap);
  }
  .preview-selector {
    display: grid;
    border: 1px solid var(--form-element-border-color);
    width: 75px;
    font-size: 0.75rem;
    height: 40px;
    align-content: center;
    justify-items: center;
    border-radius: var(--border-radius);
    /* background: #ffffff; */
    color: var(--form-element-border-color);
    cursor: pointer;
  }
  :global(.preview-selector span) {
    width: 100%;
  }
  :global(pre) {
    margin-top: 1.5em !important;
  }
</style>
